import { OperatorsButtons } from "@/components/operators/operators-tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getPromotionMonthCount,
  isEndlessPromotion,
  parsePromotion,
} from "@/lib/promotion";
import { parseMetadataNumber } from "@/lib/utils/numbers";
import { selectFetchedOrder } from "@/store/selectors/order.selector";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { persistor } from "@/store/store";
import {
  setSelectedOperator,
  setSelectedPlan,
} from "@/store/slices/operator.slice";
import { Operator } from "@/store/types/get-operators";
import { OrderResponse } from "@/store/types/order";
import { t } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface OperatorsSectionProps {
  operators: Operator[];
  loading: boolean;
  error: string | null;
}

const getSimPreselect = (order?: OrderResponse | null) => {
  const params = new URLSearchParams(window.location.search);
  const promotion = parsePromotion(order?.metadata?.promotion);
  const simOffering = promotion?.simOffering;

  const operator = (
    params.get("preselect_operator") ||
    (typeof order?.metadata?.preselect_operator === "string"
      ? order.metadata.preselect_operator
      : "") ||
    simOffering?.operatorSkuMatcher ||
    simOffering?.operatorName ||
    ""
  ).toLowerCase();

  const planSku =
    params.get("preselect_plan") ||
    (typeof order?.metadata?.preselect_plan === "string"
      ? order.metadata.preselect_plan
      : "") ||
    simOffering?.planSku ||
    "";

  return { operator, planSku, simOffering, promotion };
};

const getProductDisplayName = (order?: OrderResponse | null) => {
  const product = order?.line_items?.find((item) =>
    item.sku_code?.startsWith("R")
  );
  return product?.name?.split(",")[0]?.trim() || "";
};

export const OperatorsSection = ({
  operators,
  loading,
  error,
}: OperatorsSectionProps) => {
  const { i18n } = useTranslation();
  const order = useAppSelector(selectFetchedOrder);
  const dispatch = useAppDispatch();
  const didPreselect = useRef(false);
  const [accordionValue, setAccordionValue] = useState<string | undefined>();

  const simPreselect = useMemo(() => getSimPreselect(order), [order]);
  const shouldPreselect = Boolean(simPreselect.operator || simPreselect.planSku);

  const matchedOperator = operators.find(
    (op) =>
      op.skuMatcher?.toLowerCase().includes(simPreselect.operator) ||
      op.name?.toLowerCase().includes(simPreselect.operator) ||
      op.plans?.some((plan) => plan.sku === simPreselect.planSku)
  );
  const matchedPlan = matchedOperator?.plans.find(
    (plan) => plan.sku === simPreselect.planSku
  );

  const campaignBanner = useMemo(() => {
    const language = i18n.language?.startsWith("en") ? "en_us" : "sv_se";
    const customText =
      simPreselect.simOffering?.bannerText?.[language] ||
      simPreselect.simOffering?.bannerText?.sv_se;

    if (customText) {
      return { type: "custom" as const, text: customText };
    }

    if (!shouldPreselect) {
      const isLegacySe2020 = order?.line_items?.some(
        (item) => item.sku_code === "R1652-A-1"
      );
      return isLegacySe2020 ? { type: "legacy" as const } : null;
    }

    const operatorName =
      simPreselect.simOffering?.operatorName || matchedOperator?.name || "";
    const planName =
      simPreselect.simOffering?.planName || matchedPlan?.name || "";
    const productName = getProductDisplayName(order);
    const price = simPreselect.promotion?.appliedPrice;
    const validityPeriod = simPreselect.promotion?.validityPeriod || "";
    const bindingPeriod = parseMetadataNumber(
      order?.metadata?.["Flex Binding Period"],
      12
    );
    const months = getPromotionMonthCount(validityPeriod, bindingPeriod);

    if (!operatorName || !productName || price == null) return null;

    return {
      type: "generated" as const,
      operator: operatorName,
      plan: planName,
      product: productName,
      price,
      months,
      endless: isEndlessPromotion(validityPeriod),
    };
  }, [
    i18n.language,
    matchedOperator?.name,
    matchedPlan?.name,
    order,
    shouldPreselect,
    simPreselect,
  ]);

  useEffect(() => {
    if (!shouldPreselect) return;
    setAccordionValue("item-1");
  }, [shouldPreselect]);

  useEffect(() => {
    if (didPreselect.current || !shouldPreselect || operators.length === 0) {
      return;
    }

    const applyPreselect = () => {
      if (didPreselect.current) return;

      if (matchedOperator) {
        dispatch(setSelectedOperator(matchedOperator.skuMatcher));
        if (matchedPlan) {
          dispatch(setSelectedPlan(matchedPlan.sku));
        }
      }

      didPreselect.current = true;
      setAccordionValue("item-1");
    };

    if (persistor.getState().bootstrapped) {
      applyPreselect();
      return;
    }

    const unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) {
        applyPreselect();
      }
    });

    return unsubscribe;
  }, [dispatch, matchedOperator, matchedPlan, operators.length, shouldPreselect]);

  if (error) return null;

  return (
    <Accordion
      type="single"
      collapsible
      value={accordionValue}
      onValueChange={setAccordionValue}
    >
      <AccordionItem
        className="bg-white rounded-lg py-1 px-5 border-none"
        value="item-1"
      >
        <AccordionTrigger className="hover:no-underline font-semibold">
          {t("operators.mainTitle")}
        </AccordionTrigger>
        <AccordionContent>
          {campaignBanner && (
            <div className="bg-accent py-2 px-6 rounded-5 mb-4">
              <p className="text-white font-medium text-center">
                {campaignBanner.type === "custom" && campaignBanner.text}
                {campaignBanner.type === "legacy" && (
                  <>
                    {t("operators.hallonPromoBefore")}
                    <span className="bg-lavender-dark">
                      {t("operators.hallonPromoHighlight")}
                    </span>
                    {t("operators.hallonPromoAfter")}
                  </>
                )}
                {campaignBanner.type === "generated" && (
                  <>
                    {t("operators.simCampaignBefore", {
                      operator: campaignBanner.operator,
                      plan: campaignBanner.plan,
                    })}
                    <span className="bg-lavender-dark">
                      {t("operators.simCampaignHighlight", {
                        product: campaignBanner.product,
                        price: campaignBanner.price,
                      })}
                    </span>
                    {campaignBanner.endless
                      ? t("operators.simCampaignAfterEndless")
                      : t("operators.simCampaignAfterMonth", {
                          months: campaignBanner.months,
                        })}
                  </>
                )}
              </p>
            </div>
          )}

          {loading ? (
            <Skeleton className="w-full h-20 rounded-4" />
          ) : (
            <OperatorsButtons operators={operators} />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
